import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  Bold as BoldIcon, Italic as ItalicIcon, List, ListOrdered, Link as LinkIcon,
  ChevronDown, X, Check, Send, Loader2, AlertTriangle,
} from 'lucide-react';
import Modal from './Modal';
import TemplatePicker from './TemplatePicker';
import { wrapHtml } from './ConversationThread';
import { hasTokens } from '../../lib/broadcasts';
import { trimLeadingEmptyBlocks } from '../../lib/email-signature';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import {
  initials, firstName, shortName, avatarTone, localPart, contactRoleLabel,
  groupSenders, findSenderOption, defaultFromEmail, defaultReplyRecipients, latestOutbound,
  recordedAs, parseRecipientInput, htmlToPlainText, isBodyEmpty, replySubjectFor,
  sendErrorCopy, newClientToken, announceNeedsReplyChange,
} from '../../lib/communications';

// EmailComposer — one component for replying in a thread and for starting a
// new email to a school (Phase 2). Two variants:
//   • modal  — full form + "as the school sees it" preview + Checks card.
//   • inline — the compact reply box on the Needs-reply pane.
//
// Server contract (see functions/api/admin/communications/{compose,conversations/[id]/reply}.js):
//   200 { job:{status:'sent'}, message, conversationId }
//   202 { job:{status:'unknown', lastError} }  → poll GET send-jobs/:id every 3s
//   4xx/5xx ApiError: unknown_sender | revoked | suppressed(.data.emails) | opted_out | send_failed
// `client_token` is minted when the composer opens and reused for every retry
// of the same draft, so a double-click or a flaky network can never send the
// same email twice. A fresh token is minted after a successful send.

const SENDERS_URL = '/api/admin/communications/senders';
const POLL_MS = 3000;

const inputCls = 'w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-[13px] text-white placeholder-gray-500 focus:outline-none focus:border-[#B0FC38]/40';
const ghostBtn = 'inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const primaryBtn = 'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed';

export default function EmailComposer({
  mode = 'reply',
  variant = 'modal',
  conversation = null,
  accountId = null,
  accountName = '',
  account = null,
  initialBodyHtml = '',
  onClose,
  onSent,
  onToast,
  onResolve = null,
  resolving = false,
}) {
  const { user } = useAuth();
  const isReply = mode === 'reply' && !!conversation;
  const effectiveAccountId = accountId || conversation?.accountId || null;

  const [senders, setSenders] = useState(null);
  const [sendersError, setSendersError] = useState('');
  const [contacts, setContacts] = useState([]);
  const [fromEmail, setFromEmail] = useState('');
  const [fromMailboxId, setFromMailboxId] = useState(null);
  const fromTouched = useRef(false);
  const initialRecipients = useMemo(() => (isReply ? defaultReplyRecipients(conversation) : { to: [], cc: [] }), [isReply, conversation]);
  const [to, setTo] = useState(initialRecipients.to);
  const [cc, setCc] = useState(initialRecipients.cc);
  const [showCc, setShowCc] = useState(initialRecipients.cc.length > 0);
  const [subject, setSubject] = useState(isReply ? replySubjectFor(conversation.subject) : '');
  const [bodyHtml, setBodyHtml] = useState(initialBodyHtml || '');
  const [status, setStatus] = useState('idle'); // idle | sending | verifying | failed
  const [error, setError] = useState(null); // { title, detail, code, retryable }
  const [jobId, setJobId] = useState(null);
  const [validation, setValidation] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const clientTokenRef = useRef(newClientToken());
  const lastPayloadRef = useRef(null);
  const editorRef = useRef(null);

  const threadFrom = isReply ? latestOutbound(conversation)?.from?.email : null;
  const groups = useMemo(() => groupSenders(senders || [], { threadFrom }), [senders, threadFrom]);
  const fromOption = useMemo(() => findSenderOption(groups, fromEmail, fromMailboxId), [groups, fromEmail, fromMailboxId]);

  // Senders (the From picker) + the school's contacts (chip labels and suggestions).
  useEffect(() => {
    let alive = true;
    api.get(SENDERS_URL)
      .then((d) => { if (alive) setSenders(d?.senders || []); })
      .catch((err) => { if (alive) { setSenders([]); setSendersError(err?.message || 'Couldn’t load mailboxes'); } });
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!effectiveAccountId) return undefined;
    let alive = true;
    const p = api.get(`/api/admin/accounts/${effectiveAccountId}/contacts`);
    if (!p || typeof p.then !== 'function') return undefined;
    p.then((d) => { if (alive) setContacts(d?.contacts || []); }).catch(() => {});
    return () => { alive = false; };
  }, [effectiveAccountId]);

  // Default From once the senders arrive (unless the user already picked).
  useEffect(() => {
    if (!senders || fromTouched.current) return;
    const def = defaultFromEmail(groups, { conversation: isReply ? conversation : null });
    setFromEmail(def);
    setFromMailboxId(findSenderOption(groups, def)?.mailboxId || null);
  }, [senders, groups, isReply, conversation]);

  // Fill in names/roles for recipients we only know by address.
  useEffect(() => {
    if (contacts.length === 0) return;
    const enrich = (list) => list.map((r) => {
      if (r.name && r.role) return r;
      const c = contacts.find((x) => String(x.email || '').toLowerCase() === r.email);
      return c ? { ...r, name: r.name || c.name || '', role: r.role || c.roleTitle || contactRoleLabel(c.role), contactId: c.id } : r;
    });
    setTo((l) => enrich(l));
    setCc((l) => enrich(l));
  }, [contacts]);

  const chooseFrom = (email, option = null) => { fromTouched.current = true; setFromEmail(email); setFromMailboxId(option?.mailboxId || null); setPickerOpen(false); setError(null); };

  const suggestions = useMemo(() => {
    const taken = new Set([...to, ...cc].map((r) => r.email));
    return contacts
      .filter((c) => c.email && !taken.has(String(c.email).toLowerCase()))
      .map((c) => ({ email: String(c.email).toLowerCase(), name: c.name || '', role: c.roleTitle || contactRoleLabel(c.role), contactId: c.id }));
  }, [contacts, to, cc]);

  const optedOutAt = account?.outreachOptedOutAt || conversation?.outreachOptedOutAt || null;
  const canSendNow = status === 'idle' || status === 'failed';

  const validate = () => {
    if (!fromEmail) return 'Choose a From address.';
    if (fromOption && !fromOption.allowed) return `You can’t send from ${fromEmail}.`;
    if (to.length === 0) return 'Add at least one recipient.';
    if (!isReply && !subject.trim()) return 'Add a subject.';
    if (isBodyEmpty(bodyHtml)) return 'Write something before sending.';
    return '';
  };

  const finishSuccess = useCallback((res) => {
    setStatus('idle');
    setError(null);
    setJobId(null);
    clientTokenRef.current = newClientToken();
    lastPayloadRef.current = null;
    onToast?.({ message: `Sent from ${localPart(fromEmail)} as ${firstName(user?.name) || 'you'}` });
    announceNeedsReplyChange();
    onSent?.(res);
  }, [fromEmail, onSent, onToast, user]);

  const failWith = useCallback((err) => {
    setStatus('failed');
    setError(sendErrorCopy(err, { fromEmail }));
    const failedJobId = err?.data?.job?.id;
    if (failedJobId) setJobId(failedJobId);
  }, [fromEmail]);

  const handleJobResult = useCallback((res) => {
    const job = res?.job;
    if (!job || job.status === 'sent') { finishSuccess(res); return; }
    if (job.status === 'unknown') { setJobId(job.id); setStatus('verifying'); setError(null); return; }
    failWith({ data: res, message: job.lastError });
  }, [finishSuccess, failWith]);

  // 202 → Gmail didn't confirm. Poll the job until it lands.
  useEffect(() => {
    if (status !== 'verifying' || !jobId) return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const d = await api.get(`/api/admin/communications/send-jobs/${jobId}`);
        if (!alive) return;
        const job = d?.job;
        if (job?.status === 'sent') finishSuccess(d);
        else if (job?.status === 'failed') failWith({ data: d, message: job.lastError });
      } catch { /* keep polling */ }
    };
    const t = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [status, jobId, finishSuccess, failWith]);

  const buildPayload = () => {
    const html = bodyHtml;
    const payload = {
      from_email: fromEmail,
      mailbox_id: fromMailboxId || fromOption?.mailboxId || undefined,
      to: to.map((r) => r.email),
      body_html: html,
      body_text: htmlToPlainText(html),
      client_token: clientTokenRef.current,
    };
    if (cc.length > 0) payload.cc = cc.map((r) => r.email);
    if (!isReply) { payload.account_id = effectiveAccountId; payload.subject = subject.trim(); }
    if (hasTokens(subject) || hasTokens(html)) payload.render_tokens = true;
    return payload;
  };

  const send = async () => {
    const v = validate();
    setValidation(v);
    if (v) return;
    setStatus('sending');
    setError(null);
    const payload = buildPayload();
    lastPayloadRef.current = payload;
    try {
      const res = isReply
        ? await api.post(`/api/admin/communications/conversations/${conversation.id}/reply`, payload)
        : await api.post('/api/admin/communications/compose', payload);
      handleJobResult(res);
    } catch (err) {
      failWith(err);
    }
  };

  // Retry: a known failed job is re-run server-side; anything else re-posts
  // the same draft with the same client_token (the server dedupes on it).
  const retry = async () => {
    setStatus('sending');
    setError(null);
    try {
      if (jobId) {
        const res = await api.post(`/api/admin/communications/send-jobs/${jobId}`, {});
        handleJobResult(res);
      } else {
        const payload = lastPayloadRef.current || buildPayload();
        const res = isReply
          ? await api.post(`/api/admin/communications/conversations/${conversation.id}/reply`, payload)
          : await api.post('/api/admin/communications/compose', payload);
        handleJobResult(res);
      }
    } catch (err) {
      failWith(err);
    }
  };

  // "Insert template": subject (when empty) + body replace the draft. Tokens
  // like {{school_name}} stay literal here and are filled from the school on
  // the server (render_tokens) — the same renderer broadcasts use.
  const insertTemplate = (t) => {
    if (!isReply && !subject.trim() && t.subjectTemplate) setSubject(t.subjectTemplate);
    const html = t.bodyTemplateHtml || '<p></p>';
    editorRef.current?.commands?.setContent(html);
    setBodyHtml(t.bodyTemplateHtml || '');
    setValidation('');
  };
  const tokensInDraft = hasTokens(subject) || hasTokens(bodyHtml);

  const dropEmails = (emails) => {
    const set = new Set((emails || []).map((e) => String(e).toLowerCase()));
    setTo((l) => l.filter((r) => !set.has(r.email)));
    setCc((l) => l.filter((r) => !set.has(r.email)));
    setError(null);
    setStatus('idle');
  };

  const recorded = recordedAs(fromEmail, user?.name);
  const fromLabel = fromOption ? `${fromOption.name} <${fromOption.email}>` : (fromEmail || 'Choose a mailbox');
  const fromNote = !fromOption ? '' : fromOption.isMine
    ? 'Your mailbox'
    : `${firstName(fromOption.ownerName)}’s mailbox · you’re allowed to send as ${firstName(fromOption.ownerName)}`;

  const errorBox = error && (
    <div role="alert" className="rounded-lg border border-red-500/25 bg-red-500/[0.08] px-3 py-2.5 text-[12px]" data-testid="composer-error">
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-red-300 mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="text-red-200 font-medium">{error.title}</div>
          {error.detail && <div className="text-red-300/80 mt-0.5 break-words">{error.detail}</div>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {error.code === 'suppressed' && (error.emails || []).length > 0 && (
            <button type="button" onClick={() => dropEmails(error.emails)} className={`${ghostBtn} text-gray-200 border border-white/[0.1]`} data-testid="composer-drop-suppressed">
              Remove {error.emails.length === 1 ? 'it' : 'them'}
            </button>
          )}
          {error.retryable && (
            <button type="button" onClick={retry} disabled={status === 'sending'} className={`${ghostBtn} text-gray-200 border border-white/[0.1]`} data-testid="composer-retry">
              Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const sendingLabel = status === 'sending' ? 'Sending…' : status === 'verifying' ? 'Verifying…' : (isReply ? (variant === 'inline' ? 'Send reply' : 'Send') : 'Send');
  const sendButton = (
    <button
      type="button"
      onClick={send}
      disabled={!canSendNow || !senders}
      className={primaryBtn}
      data-testid="composer-send"
    >
      {status === 'sending' || status === 'verifying'
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />
        : <Send className="w-3.5 h-3.5" aria-hidden="true" />}
      {sendingLabel}
    </button>
  );

  const fromPicker = (
    <FromPicker
      groups={groups}
      value={fromEmail}
      selectedMailboxId={fromOption?.mailboxId || null}
      option={fromOption}
      loading={!senders}
      error={sendersError}
      open={pickerOpen}
      onOpenChange={setPickerOpen}
      onChange={chooseFrom}
      disabled={!canSendNow}
    />
  );

  const toRow = (
    <RecipientChips
      label="To"
      recipients={to}
      onChange={(l) => { setTo(l); setValidation(''); }}
      suggestions={suggestions}
      disabled={!canSendNow}
      testId="composer-to"
      trailing={!showCc && (
        <button type="button" onClick={() => setShowCc(true)} className={`${ghostBtn} ml-auto`} disabled={!canSendNow}>Cc</button>
      )}
    />
  );
  const ccRow = showCc && (
    <RecipientChips
      label="Cc"
      recipients={cc}
      onChange={setCc}
      suggestions={suggestions}
      disabled={!canSendNow}
      testId="composer-cc"
      addLabel="+ Add Cc"
    />
  );

  const body = (
    <ComposerBody
      initialHtml={initialBodyHtml}
      onChange={(html) => { setBodyHtml(html); setValidation(''); }}
      editable={canSendNow}
      compact={variant === 'inline'}
      editorRef={editorRef}
    />
  );

  const verifyingNote = status === 'verifying' && (
    <div role="status" className="text-[12px] text-amber-300 inline-flex items-center gap-1.5" data-testid="composer-verifying">
      <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" /> Verifying with Gmail — this can take a few seconds.
    </div>
  );

  if (variant === 'inline') {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-2.5" data-testid="email-composer-inline">
        <div className="flex items-center gap-2 flex-wrap text-[12px] text-gray-400">
          <span>
            Reply from <b className="text-gray-100 font-medium">{fromLabel}</b>
            {fromOption && !fromOption.isMine && <span> · {firstName(fromOption.ownerName)}’s mailbox</span>}
            {fromOption && fromOption.isMine && <span> · your mailbox</span>}
          </span>
          <button type="button" onClick={() => setPickerOpen((v) => !v)} className={`${ghostBtn} px-1.5 py-0.5`} aria-expanded={pickerOpen} disabled={!canSendNow}>change</button>
          <TemplatePicker onPick={insertTemplate} disabled={!canSendNow} className="ml-auto" />
          <span className="text-gray-500">Threads in Gmail <Check className="w-3 h-3 inline text-[#B0FC38]" aria-hidden="true" /></span>
        </div>
        {pickerOpen && <div className="pt-1">{fromPicker}</div>}
        {toRow}
        {ccRow}
        {body}
        <SignaturePreview option={fromOption} compact />
        {validation && <div className="text-[12px] text-amber-300" role="alert">{validation}</div>}
        {errorBox}
        {verifyingNote}
        <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
          <span className="text-[11.5px] text-gray-500">Will be recorded as: <span className="text-gray-300">{recorded}</span></span>
          <div className="flex items-center gap-2">
            {onResolve && (
              <button type="button" onClick={onResolve} disabled={resolving || status === 'sending'} className={`${ghostBtn} border border-white/[0.08]`}>
                {resolving ? 'Resolving…' : 'Mark resolved'}
              </button>
            )}
            {sendButton}
          </div>
        </div>
      </div>
    );
  }

  const title = isReply
    ? `Reply · ${accountName || conversation.accountName || conversation.subject || ''}`.replace(/ · $/, '')
    : `New email${accountName ? ` · ${accountName}` : ''}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      maxWidth="max-w-5xl"
      footer={
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11.5px] text-gray-500">Recorded as: <span className="text-gray-200 font-medium">{recorded}</span></span>
          <div className="flex items-center gap-2">
            {verifyingNote}
            <button type="button" onClick={onClose} className="px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white">Cancel</button>
            {sendButton}
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5" data-testid="email-composer">
        <div className="min-w-0">
          <p className="text-[12px] text-gray-500 mb-3">
            Sends through the chosen mailbox’s Gmail. It appears in that person’s Sent folder and replies come back to their inbox — and here.
          </p>
          <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] divide-y divide-white/[0.06]">
            <Row label="From">
              <div className="flex flex-col gap-1 min-w-0 w-full">
                {fromPicker}
                {fromNote && <span className="text-[11.5px] text-gray-500" data-testid="composer-from-note">{fromNote}</span>}
              </div>
            </Row>
            <Row label="To">{toRow}</Row>
            {showCc && <Row label="Cc">{ccRow}</Row>}
            <Row label="Subject">
              {isReply ? (
                <span className="text-[13px] text-white truncate" data-testid="composer-subject">{subject}</span>
              ) : (
                <input
                  className={inputCls}
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); setValidation(''); }}
                  placeholder="What this email is about"
                  aria-label="Subject"
                  data-testid="composer-subject"
                  disabled={!canSendNow}
                />
              )}
            </Row>
            <Row label="Template">
              <TemplatePicker onPick={insertTemplate} disabled={!canSendNow} />
              <span className="text-[11.5px] text-gray-500">{tokensInDraft ? 'Tokens fill in from this school when you send.' : 'Saved messages; tokens fill in per school.'}</span>
            </Row>
            <div className="p-2">{body}</div>
          </div>
          <div className="mt-3 space-y-2">
            {validation && <div className="text-[12px] text-amber-300" role="alert">{validation}</div>}
            {errorBox}
          </div>
        </div>

        <aside className="space-y-3 min-w-0">
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid="composer-preview">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-2.5">Preview · as the school sees it</div>
            <div className="text-[11.5px] text-gray-500 space-y-0.5 pb-2.5 border-b border-white/[0.06]">
              <div className="truncate">From: <b className="text-gray-200 font-medium">{fromLabel}</b></div>
              <div className="truncate">To: <b className="text-gray-200 font-medium">{to.length ? to.map((r) => shortName(r.name, r.email)).join(', ') : '—'}</b></div>
              {cc.length > 0 && <div className="truncate">Cc: <b className="text-gray-200 font-medium">{cc.map((r) => shortName(r.name, r.email)).join(', ')}</b></div>}
              <div className="truncate">Subject: <b className="text-gray-200 font-medium">{subject || '—'}</b></div>
            </div>
            <div className="mt-2.5 text-[13px] text-gray-200 leading-relaxed whitespace-pre-line break-words max-h-[320px] overflow-y-auto" data-testid="composer-preview-body">
              {htmlToPlainText(bodyHtml) || <span className="text-gray-600 italic">Nothing written yet.</span>}
            </div>
            <SignaturePreview option={fromOption} />
            <div className="mt-3 pt-2.5 border-t border-white/[0.06] text-[11px] text-gray-600">Service correspondence — no marketing footer.</div>
          </div>
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3.5" data-testid="composer-checks">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1.5">Checks</div>
            <KV label="Suppression list"><span className="text-gray-300">Checked on send</span></KV>
            <KV label="Opted out">
              {optedOutAt
                ? <span className="text-amber-300">Yes · one-to-one replies still go through</span>
                : <span className="text-[#B0FC38]">No</span>}
            </KV>
            <KV label="Threads in Gmail" last>
              <span className="text-[#B0FC38]">{isReply ? 'Yes · same thread' : 'New thread'}</span>
            </KV>
          </div>
        </aside>
      </div>
    </Modal>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[64px_minmax(0,1fr)] gap-3 items-start px-3 py-2.5">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 pt-1.5">{label}</span>
      <div className="min-w-0 flex items-center gap-2 flex-wrap">{children}</div>
    </div>
  );
}

// The chosen alias's Gmail signature, as the server will append it — with the
// blank lines Gmail signatures often open with trimmed, as the send does
// (trimLeadingEmptyBlocks mirrors the server's). Untrusted HTML (it comes from
// Google), so it only ever renders inside a sandboxed iframe — never
// dangerouslySetInnerHTML. The compact (inline) variant keeps the caption and
// hides the frame behind a toggle so the reply box stays short.
export function SignaturePreview({ option, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!option) return null;
  const alias = option.email;
  if (!option.signature) {
    return (
      <div className={`text-[11px] text-gray-600 ${compact ? '' : 'mt-2.5 pt-2.5 border-t border-white/[0.06]'}`} data-testid="composer-signature-none">
        No Gmail signature set for {alias}
      </div>
    );
  }
  const frame = (
    <iframe
      title={`Gmail signature for ${alias}`}
      sandbox=""
      srcDoc={wrapHtml(trimLeadingEmptyBlocks(option.signature))}
      className={`w-full rounded-md border border-white/[0.08] bg-white ${compact ? 'h-[110px]' : 'h-[150px]'}`}
      data-testid="composer-signature-frame"
    />
  );
  return (
    <div className={compact ? '' : 'mt-2.5 pt-2.5 border-t border-white/[0.06]'} data-testid="composer-signature">
      <div className="flex items-center justify-between gap-2 text-[11px] text-gray-500">
        <span>Signature for <span className="text-gray-300">{alias}</span> · added automatically</span>
        {compact && (
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className={`${ghostBtn} px-1.5 py-0.5`} data-testid="composer-signature-toggle">
            {open ? 'Hide' : 'Show'}
          </button>
        )}
      </div>
      {(!compact || open) && <div className="mt-1.5">{frame}</div>}
    </div>
  );
}

function KV({ label, children, last = false }) {
  return (
    <div className={`flex items-center justify-between gap-3 py-1.5 text-[12px] ${last ? '' : 'border-b border-white/[0.05]'}`}>
      <span className="text-gray-500">{label}</span>
      <span className="text-right">{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

export function FromPicker({ groups, value, option, loading, error, open, onOpenChange, onChange, disabled, selectedMailboxId = null }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onOpenChange(false); };
    const onKey = (e) => { if (e.key === 'Escape') onOpenChange(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open, onOpenChange]);

  const label = loading ? 'Loading mailboxes…' : option ? `${option.name} <${option.email}>` : (value || 'Choose a mailbox');
  const noneAllowed = !loading && groups.every((g) => !g.allowed);

  return (
    <div className="relative min-w-0" ref={ref}>
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="From"
        disabled={disabled || loading}
        data-testid="from-picker"
        className="inline-flex items-center gap-2 max-w-full px-2.5 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] text-[13px] text-white hover:border-white/[0.15] disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {option && <span aria-hidden="true" className={`w-5 h-5 rounded-full inline-grid place-items-center text-[9px] font-semibold shrink-0 ${avatarTone(option.ownerName)}`}>{initials(option.ownerName)}</span>}
        <span className="truncate">{label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-gray-500 shrink-0" aria-hidden="true" />
      </button>
      {error && <div className="text-[11.5px] text-red-300 mt-1">{error}</div>}
      {!error && noneAllowed && (
        <div className="text-[11.5px] text-amber-300 mt-1">No connected mailbox you can send from. Connect Gmail on the Mailboxes page.</div>
      )}
      {open && (
        <div role="listbox" aria-label="Send from" className="absolute left-0 top-full mt-1.5 z-20 w-[min(92vw,380px)] max-h-[320px] overflow-y-auto rounded-xl border border-white/[0.1] bg-[#0f0f16] shadow-[0_20px_60px_-10px_rgba(0,0,0,0.8)] p-1.5">
          {groups.length === 0 && <div className="px-2.5 py-2 text-[12px] text-gray-500">No connected mailboxes.</div>}
          {groups.map((g) => (
            <div key={g.mailboxId} role="group" aria-label={g.label} data-testid={`from-group-${g.mailboxId}`}>
              <div className="px-2.5 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500">{g.label}</div>
              {g.options.map((o) => {
                const selected = o.value === value && (!selectedMailboxId || o.mailboxId === selectedMailboxId);
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    aria-disabled={!o.allowed}
                    disabled={!o.allowed}
                    onClick={() => o.allowed && onChange(o.value, o)}
                    data-testid={`from-option-${o.value}`}
                    data-mailbox={o.mailboxId}
                    className={`w-full text-left flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[13px] ${selected ? 'bg-[#B0FC38]/[0.12] text-white' : 'text-gray-200 hover:bg-white/[0.04]'} disabled:opacity-45 disabled:cursor-not-allowed`}
                  >
                    <span aria-hidden="true" className={`w-[22px] h-[22px] rounded-full inline-grid place-items-center text-[9px] font-semibold shrink-0 ${avatarTone(o.ownerName)}`}>{initials(o.ownerName)}</span>
                    <span className="min-w-0 flex-1 truncate">
                      {o.name} <span className="text-gray-500">{o.email}</span>
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border whitespace-nowrap ${o.allowed ? 'border-white/[0.08] text-gray-400' : 'border-amber-500/30 text-amber-300/80'}`}>{o.tag}</span>
                    {selected && <Check className="w-3.5 h-3.5 text-[#B0FC38] shrink-0" aria-hidden="true" />}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

export function RecipientChips({ label, recipients, onChange, suggestions = [], disabled = false, testId, addLabel = '+ Add', trailing = null }) {
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [invalid, setInvalid] = useState('');
  const inputRef = useRef(null);

  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

  const add = (list) => {
    const seen = new Set(recipients.map((r) => r.email));
    const next = [...recipients];
    for (const r of list) {
      if (seen.has(r.email)) continue;
      seen.add(r.email);
      const s = suggestions.find((x) => x.email === r.email);
      next.push(s ? { ...s, ...r, name: r.name || s.name, role: r.role || s.role } : r);
    }
    onChange(next);
  };

  const commit = () => {
    if (!text.trim()) { setAdding(false); setInvalid(''); return; }
    const { recipients: ok, invalid: bad } = parseRecipientInput(text);
    if (ok.length) add(ok);
    if (bad.length) { setInvalid(`Not an email address: ${bad.join(', ')}`); setText(bad.join(', ')); }
    else { setText(''); setInvalid(''); setAdding(false); }
  };

  const filtered = text.trim()
    ? suggestions.filter((s) => `${s.name} ${s.email} ${s.role}`.toLowerCase().includes(text.trim().toLowerCase()))
    : suggestions;

  return (
    <div className="flex items-center gap-1.5 flex-wrap w-full" data-testid={testId}>
      {label && <span className="sr-only">{label}</span>}
      {recipients.map((r) => (
        <span key={r.email} className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-white/[0.1] bg-white/[0.04] text-[12px] text-gray-100" data-testid="recipient-chip" title={r.email}>
          <span>{r.name ? shortName(r.name, r.email) : r.email}</span>
          {r.role && <small className="text-gray-500 text-[10.5px]">{r.role}</small>}
          <button
            type="button"
            onClick={() => onChange(recipients.filter((x) => x.email !== r.email))}
            aria-label={`Remove ${r.email}`}
            disabled={disabled}
            className="p-0.5 rounded-full text-gray-500 hover:text-white hover:bg-white/[0.08] disabled:opacity-40"
          >
            <X className="w-3 h-3" aria-hidden="true" />
          </button>
        </span>
      ))}
      {adding ? (
        <div className="relative min-w-[200px] flex-1">
          <input
            ref={inputRef}
            value={text}
            onChange={(e) => { setText(e.target.value); setInvalid(''); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setAdding(false); setText(''); setInvalid(''); }
            }}
            onBlur={() => setTimeout(commit, 120)}
            placeholder="name@school.org"
            aria-label={`Add ${label || 'recipient'}`}
            aria-invalid={!!invalid}
            className={`${inputCls} py-1 text-[12px]`}
            data-testid={`${testId}-input`}
          />
          {invalid && <div className="text-[11px] text-amber-300 mt-1">{invalid}</div>}
          {filtered.length > 0 && (
            <ul className="absolute left-0 top-full mt-1 z-20 w-full max-h-[200px] overflow-y-auto rounded-lg border border-white/[0.1] bg-[#0f0f16] shadow-lg p-1 list-none m-0" role="listbox" aria-label={`${label || 'Recipient'} suggestions`}>
              {filtered.slice(0, 8).map((s) => (
                <li key={s.email}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { add([s]); setText(''); setInvalid(''); setAdding(false); }}
                    className="w-full text-left px-2.5 py-1.5 rounded-md text-[12px] text-gray-200 hover:bg-white/[0.05] flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{s.name || s.email}{s.role ? <span className="text-gray-500"> · {s.role}</span> : null}</span>
                    <span className="text-gray-500 truncate">{s.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} disabled={disabled} className={`${ghostBtn} px-2 py-1`} data-testid={`${testId}-add`}>{addLabel}</button>
      )}
      {trailing}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Body editor — the same TipTap setup as TemplateEditor (StarterKit + Link),
// trimmed to what a reply needs: bold, italic, lists, links.

function ComposerBody({ initialHtml, onChange, editable = true, compact = false, editorRef }) {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');
  const editor = useEditor({
    // StarterKit v3 bundles Link; configure it there rather than adding a
    // second copy (TipTap warns about duplicate extension names).
    extensions: [
      StarterKit.configure({ link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' } } }),
    ],
    content: initialHtml || '<p></p>',
    editable,
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });
  useEffect(() => { if (editorRef) editorRef.current = editor; }, [editor, editorRef]);
  useEffect(() => { if (editor && editor.isEditable !== editable) editor.setEditable(editable); }, [editor, editable]);

  const applyLink = () => {
    if (!editor) return;
    const raw = linkHref.trim();
    if (!raw) { setLinkOpen(false); return; }
    const url = /^(https?:|mailto:|tel:)/i.test(raw) ? raw : `https://${raw}`;
    const { from, to } = editor.state.selection;
    if (from === to) editor.chain().focus().insertContent(`<a href="${url}">${url.replace(/</g, '&lt;')}</a>`).run();
    else editor.chain().focus().setLink({ href: url }).run();
    setLinkHref('');
    setLinkOpen(false);
  };

  const Btn = ({ active, onClick, title, children, testId }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={!editable}
      data-testid={testId}
      className={`p-1.5 rounded ${active ? 'bg-[#B0FC38]/20 text-white' : 'text-gray-400 hover:text-white'} disabled:opacity-40`}
    >
      {children}
    </button>
  );

  return (
    <div className="rounded-lg border border-white/[0.08] bg-white/[0.02]" data-testid="composer-body">
      <style>{`
        .composer-editor p { margin: 0 0 0.5em 0; }
        .composer-editor ul { list-style: disc; padding-left: 1.5em; margin: 0.5em 0; }
        .composer-editor ol { list-style: decimal; padding-left: 1.5em; margin: 0.5em 0; }
        .composer-editor li > p { margin: 0; }
        .composer-editor strong { font-weight: 600; }
        .composer-editor a { color: #93c5fd; text-decoration: underline; }
        .composer-editor :focus { outline: none; }
        .composer-editor .ProseMirror { min-height: ${compact ? '96px' : '180px'}; }
      `}</style>
      <div className="composer-editor px-3 py-2.5 text-[13px] text-gray-100 leading-relaxed" data-testid="composer-editor">
        <EditorContent editor={editor} />
      </div>
      <div className="flex items-center gap-0.5 px-2 py-1 border-t border-white/[0.05] flex-wrap">
        <Btn title="Bold" testId="composer-bold" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}><BoldIcon className="w-3.5 h-3.5" /></Btn>
        <Btn title="Italic" testId="composer-italic" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}><ItalicIcon className="w-3.5 h-3.5" /></Btn>
        <Btn title="Bulleted list" active={editor?.isActive('bulletList')} onClick={() => editor?.chain().focus().toggleBulletList().run()}><List className="w-3.5 h-3.5" /></Btn>
        <Btn title="Numbered list" active={editor?.isActive('orderedList')} onClick={() => editor?.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-3.5 h-3.5" /></Btn>
        <Btn title="Link" active={editor?.isActive('link') || linkOpen} onClick={() => setLinkOpen((v) => !v)}><LinkIcon className="w-3.5 h-3.5" /></Btn>
        {linkOpen && (
          <div className="flex items-center gap-1.5 ml-1">
            <input
              value={linkHref}
              onChange={(e) => setLinkHref(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyLink(); } if (e.key === 'Escape') setLinkOpen(false); }}
              placeholder="https://…"
              aria-label="Link URL"
              className={`${inputCls} py-1 text-[12px] w-[220px]`}
            />
            <button type="button" onClick={applyLink} className={`${ghostBtn} px-2 py-1`}>Apply</button>
          </div>
        )}
      </div>
    </div>
  );
}
